import { useCallback, useMemo, useState } from 'react'
import { activateLicense, extendLicense, suspendLicense, updateLicense } from '../../services/admin'
import { activateAndCreateLicense, createEntitlementFromCriteria } from '../../services/licensing'
import type {
  License,
  LicenseStatus,
  LicenseType,
  Organisation,
  UpdateLicenseRequest,
} from '../../types'

interface LicensesTabStateInput {
  licenses: License[]
  organisations: Organisation[]
  onRefresh: () => Promise<void>
}

export interface LicensesFilters {
  searchQuery: string
  setSearchQuery: (value: string) => void
  filterStatus: LicenseStatus | ''
  setFilterStatus: (value: LicenseStatus | '') => void
  filterType: LicenseType | ''
  setFilterType: (value: LicenseType | '') => void
  filterOrg: string
  setFilterOrg: (value: string) => void
}

export interface LicensesModalSlots {
  showCreateModal: boolean
  setShowCreateModal: (value: boolean) => void
  editingLicense: License | null
  setEditingLicense: (value: License | null) => void
  extendingLicense: License | null
  setExtendingLicense: (value: License | null) => void
  suspendingLicense: License | null
  setSuspendingLicense: (value: License | null) => void
  activatingLicense: License | null
  setActivatingLicense: (value: License | null) => void
}

export interface LicensesActions {
  handleCreateLicenseFromGates: (orgId: string, xeroId: string, criteriaId: string) => Promise<void>
  handleUpdateLicense: (data: UpdateLicenseRequest) => Promise<void>
  handleExtendLicense: (validUntil: string) => Promise<void>
  handleActivateLicense: () => Promise<void>
  handleSuspendLicense: () => Promise<void>
}

export interface LicensesTabState {
  filters: LicensesFilters
  modals: LicensesModalSlots
  actions: LicensesActions
  filteredLicenses: License[]
  getOrgName: (orgId: string) => string
}

function useFiltersState(): LicensesFilters {
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState<LicenseStatus | ''>('')
  const [filterType, setFilterType] = useState<LicenseType | ''>('')
  const [filterOrg, setFilterOrg] = useState('')
  return {
    searchQuery,
    setSearchQuery,
    filterStatus,
    setFilterStatus,
    filterType,
    setFilterType,
    filterOrg,
    setFilterOrg,
  }
}

function useModalSlots(): LicensesModalSlots {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingLicense, setEditingLicense] = useState<License | null>(null)
  const [extendingLicense, setExtendingLicense] = useState<License | null>(null)
  const [suspendingLicense, setSuspendingLicense] = useState<License | null>(null)
  const [activatingLicense, setActivatingLicense] = useState<License | null>(null)
  return {
    showCreateModal,
    setShowCreateModal,
    editingLicense,
    setEditingLicense,
    extendingLicense,
    setExtendingLicense,
    suspendingLicense,
    setSuspendingLicense,
    activatingLicense,
    setActivatingLicense,
  }
}

interface FilterApplyInput {
  licenses: License[]
  filters: LicensesFilters
  getOrgName: (orgId: string) => string
}

function applyFilters({ licenses, filters, getOrgName }: FilterApplyInput): License[] {
  return licenses.filter((license) => {
    if (filters.searchQuery) {
      const search = filters.searchQuery.toLowerCase()
      const orgName = getOrgName(license.organisationId).toLowerCase()
      if (!license.licenseKey.toLowerCase().includes(search) && !orgName.includes(search)) {
        return false
      }
    }
    if (filters.filterStatus && license.status !== filters.filterStatus) return false
    if (filters.filterType && license.licenseType !== filters.filterType) return false
    if (filters.filterOrg && license.organisationId !== filters.filterOrg) return false
    return true
  })
}

interface ActionsInput {
  modals: LicensesModalSlots
  onRefresh: () => Promise<void>
}

function buildActions({ modals, onRefresh }: ActionsInput): LicensesActions {
  const handleCreateLicenseFromGates = async (
    orgId: string,
    xeroId: string,
    criteriaId: string
  ): Promise<void> => {
    const entitlement = await createEntitlementFromCriteria({
      organisationId: orgId,
      licenseCriteriaId: criteriaId,
      xeroInvoiceStateId: xeroId,
    })
    await activateAndCreateLicense(entitlement.id)
    await onRefresh()
  }
  const handleUpdateLicense = async (data: UpdateLicenseRequest): Promise<void> => {
    if (!modals.editingLicense) return
    await updateLicense(modals.editingLicense.id, data)
    await onRefresh()
  }
  const handleExtendLicense = async (validUntil: string): Promise<void> => {
    if (!modals.extendingLicense) return
    await extendLicense(modals.extendingLicense.id, { validUntil })
    await onRefresh()
  }
  const handleActivateLicense = async (): Promise<void> => {
    if (!modals.activatingLicense) return
    await activateLicense(modals.activatingLicense.id)
    await onRefresh()
  }
  const handleSuspendLicense = async (): Promise<void> => {
    if (!modals.suspendingLicense) return
    await suspendLicense(modals.suspendingLicense.id)
    await onRefresh()
  }
  return {
    handleCreateLicenseFromGates,
    handleUpdateLicense,
    handleExtendLicense,
    handleActivateLicense,
    handleSuspendLicense,
  }
}

export function useLicensesTabState(input: LicensesTabStateInput): LicensesTabState {
  const { licenses, organisations, onRefresh } = input
  const filters = useFiltersState()
  const modals = useModalSlots()
  const getOrgName = useCallback(
    (orgId: string): string => organisations.find((org) => org.id === orgId)?.name ?? 'Unknown',
    [organisations]
  )
  const filteredLicenses = useMemo(
    () => applyFilters({ licenses, filters, getOrgName }),
    [licenses, filters, getOrgName]
  )
  const actions = buildActions({ modals, onRefresh })
  return { filters, modals, actions, filteredLicenses, getOrgName }
}
